import styled from 'styled-components';
import { useAppDispatch } from '../../../../redux/hooks';
import SchemaSubjectsList from './SchemaSubjectsList';
import {
  InputField,
  TextAreaField,
  VerticalField,
} from '../../../../components/Form';
import React, { useCallback } from 'react';
import {
  ConfigurationColsWrapper,
  ConfigurationForm,
} from './SchemaConfigValuesUI';
import {
  dataModelerUpdateCurrentSchemaDescription,
  dataModelerUpdateCurrentSchemaName,
} from '../../../../redux/slices/dataModeler';
import { autoSchemaKeyFromName } from '../utils';
import { useNavigate } from 'react-router-dom';
import { SCHEMA_NAME_MAX_LENGTH, useSchemaNameValidator } from '../validation';
import { appRoutes } from '../../../../path';
import SchemaEnableDisable from './SchemaEnableDisable';
import FunnelPartitionsList from './FunnelPartitionsList';
import { WeekStart } from '../../../../utils/apollo/graphqlSchema';
import { ConfigurationValue, VerticalWrapper } from '../DataModelerCommonUI';
import { DataModelerConfigLabelWithInfo } from '../DataModelerShared';
import { dataModelLabelInfoTooltipOptions } from '../../../../components/KubitTooltip/globalTooltips';
import ThinDivider from '../../../../components/ThinDivider';
import { UnreachableCaseError } from 'ts-essentials';
import { useDataModelerContext } from '../../DataModelerContext';

const weekStartText = (weekStart?: WeekStart): string => {
  if (!weekStart) {
    return 'Monday';
  }
  switch (weekStart) {
    case WeekStart.Monday:
      return 'Monday';
    case WeekStart.Sunday:
      return 'Sunday';
    default:
      throw new UnreachableCaseError(weekStart);
  }
};

const InputsWrapper = styled(VerticalWrapper)`
  width: 100%;
  max-width: 280px;
`;

const SchemaConfigValues = () => {
  const dispatch = useAppDispatch();
  const { draftEdits, schemaObj, isEditMode } = useDataModelerContext();
  const navigate = useNavigate();
  const currentName = draftEdits?.updatedName ?? schemaObj?.name ?? '';
  const { schemaNameValidator, knownSchemaKeys } =
    useSchemaNameValidator(currentName);

  const getNameErrors = useCallback(
    (value: string) => {
      return (
        schemaNameValidator
          .safeParse({ value })
          .error?.errors.map((issue) => issue.message)
          .join('\n') ?? null
      );
    },
    [schemaNameValidator],
  );

  const setName = (newName: string) => {
    const newKey = draftEdits?.isNew
      ? autoSchemaKeyFromName(newName, knownSchemaKeys)
      : null;
    dispatch(
      dataModelerUpdateCurrentSchemaName({
        newName,
        newKey,
        nameError: getNameErrors(newName),
      }),
    );
    if (newKey && schemaObj?.key !== newKey) {
      navigate(appRoutes.modeler.modeler(newKey));
    }
  };

  const setDescription = useCallback(
    (newDescription: string) => {
      dispatch(dataModelerUpdateCurrentSchemaDescription({ newDescription }));
    },
    [dispatch],
  );
  const currentDescription =
    draftEdits?.description ?? schemaObj?.description ?? '';

  const errors = draftEdits?.nameError;

  return (
    <ConfigurationForm>
      <ConfigurationColsWrapper>
        <InputsWrapper>
          <VerticalField noMargin={true}>
            <DataModelerConfigLabelWithInfo
              labelText={'Schema Name'}
              tooltipOption={dataModelLabelInfoTooltipOptions.schemaName}
            />
            <InputField
              disabled={!isEditMode}
              type={'text'}
              value={currentName ?? ''}
              onChange={setName}
              hasError={!!errors}
              maxLength={SCHEMA_NAME_MAX_LENGTH}
            />
          </VerticalField>
          <VerticalField noMargin={true}>
            <DataModelerConfigLabelWithInfo
              labelText={'Schema Description'}
              tooltipOption={dataModelLabelInfoTooltipOptions.schemaDescription}
            />
            <TextAreaField
              disabled={!isEditMode}
              value={currentDescription}
              onChange={setDescription}
              minRows={4}
              maxRows={4}
              maxLength={512}
            />
          </VerticalField>
          <VerticalField noMargin={true}>
            <DataModelerConfigLabelWithInfo
              labelText={'Timezone'}
              tooltipOption={dataModelLabelInfoTooltipOptions.timezone}
            />
            <InputField
              disabled={true}
              type={'text'}
              value={schemaObj?.dsConfig.timezone ?? 'UTC'}
            />
          </VerticalField>
          <VerticalField noMargin={true}>
            <DataModelerConfigLabelWithInfo
              labelText={'Week Start'}
              tooltipOption={dataModelLabelInfoTooltipOptions.weekStart}
            />
            <InputField
              disabled={true}
              type={'text'}
              value={weekStartText(schemaObj?.dsConfig.weekStart)}
            />
          </VerticalField>
        </InputsWrapper>
        <VerticalWrapper>
          <VerticalField noMargin={true}>
            <DataModelerConfigLabelWithInfo
              labelText={'Schema Key'}
              tooltipOption={dataModelLabelInfoTooltipOptions.schemaKey}
            />
            <ConfigurationValue>{schemaObj?.key}</ConfigurationValue>
          </VerticalField>
          <SchemaSubjectsList />
        </VerticalWrapper>
        <SchemaEnableDisable />
      </ConfigurationColsWrapper>
      <ThinDivider size="full" layout="horizontal" />
      <FunnelPartitionsList />
    </ConfigurationForm>
  );
};

export default SchemaConfigValues;
